/**
 * Pure helpers for Mini Quadra. All units are tennis-court "proportional units"
 * — singles court is 27 wide x 78 long, doubles adds 4.5 on each side (36 wide).
 *
 * Ported from the React prototype at `design quadra/Mini Quadra.html` to
 * idiomatic TS so it can be unit-tested without a DOM.
 */
import type { CourtZoneLabel, CourtImpact, TrailPoint } from './types.js';

export const COURT = {
  W: 27, // singles width
  L: 78, // full length
  DOUBLES_W: 36,
  SERVICE_Y: 21, // distance from net to service line
  CENTER_X: 13.5,
} as const;

export interface ZoneRect {
  readonly id: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Zones for a half court. Coordinates are relative to singles (0..27 wide). */
export function zonesForHalf(half: 'top' | 'bottom'): ZoneRect[] {
  const net = COURT.L / 2;
  const serviceLine = half === 'top' ? net - COURT.SERVICE_Y : net + COURT.SERVICE_Y;
  const baseline = half === 'top' ? 0 : COURT.L;
  const mid = COURT.CENTER_X;
  const deuceLeft = half === 'top';

  const yServiceTop = Math.min(serviceLine, net);
  const yServiceBot = Math.max(serviceLine, net);

  return [
    {
      id: 'deuce',
      label: 'DEUCE',
      x: deuceLeft ? 0 : mid,
      y: yServiceTop,
      w: mid,
      h: yServiceBot - yServiceTop,
    },
    {
      id: 'ad',
      label: 'AD',
      x: deuceLeft ? mid : 0,
      y: yServiceTop,
      w: mid,
      h: yServiceBot - yServiceTop,
    },
    {
      id: 'backL',
      label: 'WIDE',
      x: 0,
      y: half === 'top' ? baseline : serviceLine,
      w: mid / 2,
      h: half === 'top' ? serviceLine : baseline - serviceLine,
    },
    {
      id: 'body',
      label: 'BODY',
      x: mid / 2,
      y: half === 'top' ? baseline : serviceLine,
      w: mid,
      h: half === 'top' ? serviceLine : baseline - serviceLine,
    },
    {
      id: 'backR',
      label: 'WIDE',
      x: mid + mid / 2,
      y: half === 'top' ? baseline : serviceLine,
      w: mid / 2,
      h: half === 'top' ? serviceLine : baseline - serviceLine,
    },
  ];
}

/** Classify a point (singles coords) into a tennis zone. */
export function detectZone(x: number, y: number): CourtZoneLabel {
  const net = COURT.L / 2;
  const half = y < net ? 'top' : 'bottom';
  const serviceNear = Math.abs(y - net) <= COURT.SERVICE_Y;
  const mid = COURT.CENTER_X;

  if (serviceNear) {
    const deuceLeft = half === 'top';
    const onLeft = x < mid;
    if ((deuceLeft && onLeft) || (!deuceLeft && !onLeft)) return 'DEUCE';
    return 'AD';
  }
  if (x < mid / 2) return 'WIDE-L';
  if (x > mid + mid / 2) return 'WIDE-R';
  return 'BODY';
}

export function zoneToId(zone: CourtZoneLabel, y: number): string {
  const half = y < COURT.L / 2 ? 'top' : 'bot';
  if (zone === 'DEUCE') return `${half}-deuce`;
  if (zone === 'AD') return `${half}-ad`;
  if (zone === 'WIDE-L') return `${half}-backL`;
  if (zone === 'WIDE-R') return `${half}-backR`;
  return `${half}-body`;
}

/** Surface gradient CSS variable names. */
export function surfaceFill(surface: 'grass' | 'clay' | 'hard'): { a: string; b: string } {
  if (surface === 'grass') return { a: 'var(--grass-a)', b: 'var(--grass-b)' };
  if (surface === 'clay') return { a: 'var(--clay-a)', b: 'var(--clay-b)' };
  return { a: 'var(--hard-a)', b: 'var(--hard-b)' };
}

/** Isometric projection used by the IsoCourt variant. */
export function iso(x: number, y: number): { X: number; Y: number } {
  const cos30 = Math.cos(Math.PI / 6);
  const sin30 = Math.sin(Math.PI / 6);
  return { X: (x - y) * cos30, Y: (x + y) * sin30 * 0.55 };
}

/**
 * Deterministic tiny PRNG (mulberry32). Same seed => same sequence — used in
 * SwingDetail to place a mock impact per swing without DB schema changes.
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Given a seed (e.g. swing index combined with session hash), produce a
 * stable impact in singles coords for a given player.
 */
export function deterministicImpact(
  seed: number,
  player: 1 | 2,
  shotType: string,
  speed: number,
): CourtImpact {
  const rng = mulberry32(seed);
  const targetHalf = player === 1 ? 'top' : 'bottom';
  const baseY =
    targetHalf === 'top'
      ? 2 + rng() * (COURT.L / 2 - 4)
      : COURT.L / 2 + 2 + rng() * (COURT.L / 2 - 4);
  const x = 1 + rng() * (COURT.W - 2);
  const zone = detectZone(x, baseY);
  return {
    id: `swing-${seed}`,
    x,
    y: baseY,
    player,
    zone,
    speed,
    shotType,
    // Use the seed as the timestamp so two calls with identical seeds produce
    // fully identical impacts. Real ball-tracking impacts in Sprint 3 will
    // replace this with a real event timestamp.
    t: seed,
  };
}

export function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

export type { TrailPoint };
